import { TypographyOptions } from '@mui/material/styles/createTypography'

const font = ['Inter', 'sans-serif'].join(',')

enum FontWeight {
	Light = 300,
	Regular = 400,
	SemiBold = 600,
}

const light = FontWeight.Light
const regular = FontWeight.Regular
const semiBold = FontWeight.SemiBold

const breakpoints = {
	mobile: '@media (max-width:600px)',
}

const pxToRem = (pixels: number, htmlFontSize = 16): string => {
	return `${pixels / htmlFontSize}rem`
}

export const typescale = {
	family: font,
	weight: { light, regular, semiBold },
	tripleLarge: {
		fontSize: pxToRem(40),
		lineHeight: 1.2, // 48 / 40 = 1.2
	},
	tripleLargeMobile: {
		fontSize: pxToRem(36),
		lineHeight: 1.22, // 44 / 36 = 1.22
	},
	doubleLarge: {
		fontSize: pxToRem(36),
		lineHeight: 1.22, // 44 / 36 = 1.22
	},
	doubleLargeMobile: {
		fontSize: pxToRem(32),
		lineHeight: 1.25, // 40 / 32 = 1.25
	},
	extraLarge: {
		fontSize: pxToRem(32),
		lineHeight: 1.25, // 40 / 32 = 1.25
	},
	extraLargeMobile: {
		fontSize: pxToRem(28),
		lineHeight: 1.29, // 36 / 28 = 1.29
	},
	large: {
		fontSize: pxToRem(28),
		lineHeight: 1.29, // 36 / 28 = 1.29
	},
	largeMobile: {
		fontSize: pxToRem(24),
		lineHeight: 1.33, // 32 / 24 = 1.33
	},
	mediumPlus: {
		fontSize: pxToRem(24),
		lineHeight: 1.33, // 32 / 24 = 1.33
	},
	mediumPlusMobile: {
		fontSize: pxToRem(20),
		lineHeight: 1.4, // 28 / 20 = 1.4
	},
	medium: {
		fontSize: pxToRem(20),
		lineHeight: 1.4, // 28 / 20 = 1.4
	},
	mediumMobile: {
		fontSize: pxToRem(18),
		lineHeight: 1.33, //  24 / 18 = 1.33
	},
	basePlus: {
		fontSize: pxToRem(18),
		lineHeight: 1.33, // 24 / 18 = 1.33
	},
	basePlusMobile: {
		fontSize: pxToRem(17),
		lineHeight: 1.29, // 22 / 17 = 1.29
	},
	base: {
		fontSize: pxToRem(16),
		lineHeight: 1.25, // 20 / 16 = 1.25
	},
	baseMobile: {
		fontSize: pxToRem(15),
		lineHeight: 1.33, // 20 / 15 = 1.33
	},
	small: {
		fontSize: pxToRem(14),
		lineHeight: 1.43, // 20 / 14 = 1.43
	},
	smallMobile: {
		fontSize: pxToRem(13),
		lineHeight: 1.231, // 16 / 13 = 1.231
	},
	extraSmall: {
		fontSize: pxToRem(12),
		lineHeight: 1.33, // 16 / 12 = 1.33
	},
} as const

export const typography: TypographyOptions = {
	fontFamily: font,
	tripleLargeLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.tripleLarge.fontSize,
		lineHeight: typescale.tripleLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.tripleLargeMobile.fontSize,
			lineHeight: typescale.tripleLargeMobile.lineHeight,
		},
	},
	tripleLargeRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.tripleLarge.fontSize,
		lineHeight: typescale.tripleLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.tripleLargeMobile.fontSize,
			lineHeight: typescale.tripleLargeMobile.lineHeight,
		},
	},
	tripleLargeSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.tripleLarge.fontSize,
		lineHeight: typescale.tripleLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.tripleLargeMobile.fontSize,
			lineHeight: typescale.tripleLargeMobile.lineHeight,
		},
	},
	doubleLargeLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.doubleLarge.fontSize,
		lineHeight: typescale.doubleLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.doubleLargeMobile.fontSize,
			lineHeight: typescale.doubleLargeMobile.lineHeight,
		},
	},
	doubleLargeRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.doubleLarge.fontSize,
		lineHeight: typescale.doubleLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.doubleLargeMobile.fontSize,
			lineHeight: typescale.doubleLargeMobile.lineHeight,
		},
	},
	doubleLargeSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.doubleLarge.fontSize,
		lineHeight: typescale.doubleLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.doubleLargeMobile.fontSize,
			lineHeight: typescale.doubleLargeMobile.lineHeight,
		},
	},
	extraLargeLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.extraLarge.fontSize,
		lineHeight: typescale.extraLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.extraLargeMobile.fontSize,
			lineHeight: typescale.extraLargeMobile.lineHeight,
		},
	},
	extraLargeRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.extraLarge.fontSize,
		lineHeight: typescale.extraLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.extraLargeMobile.fontSize,
			lineHeight: typescale.extraLargeMobile.lineHeight,
		},
	},
	extraLargeSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.extraLarge.fontSize,
		lineHeight: typescale.extraLarge.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.extraLargeMobile.fontSize,
			lineHeight: typescale.extraLargeMobile.lineHeight,
		},
	},
	largeLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.large.fontSize,
		lineHeight: typescale.large.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.largeMobile.fontSize,
			lineHeight: typescale.largeMobile.lineHeight,
		},
	},
	largeRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.large.fontSize,
		lineHeight: typescale.large.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.largeMobile.fontSize,
			lineHeight: typescale.largeMobile.lineHeight,
		},
	},
	largeSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.large.fontSize,
		lineHeight: typescale.large.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.largeMobile.fontSize,
			lineHeight: typescale.largeMobile.lineHeight,
		},
	},
	mediumLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.medium.fontSize,
		lineHeight: typescale.medium.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.mediumMobile.fontSize,
			lineHeight: typescale.mediumMobile.lineHeight,
		},
	},
	mediumRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.medium.fontSize,
		lineHeight: typescale.medium.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.mediumMobile.fontSize,
			lineHeight: typescale.mediumMobile.lineHeight,
		},
	},
	mediumSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.medium.fontSize,
		lineHeight: typescale.medium.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.mediumMobile.fontSize,
			lineHeight: typescale.mediumMobile.lineHeight,
		},
	},
	mediumPlusSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.mediumPlus.fontSize,
		lineHeight: typescale.mediumPlus.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.mediumPlusMobile.fontSize,
			lineHeight: typescale.mediumPlusMobile.lineHeight,
		},
	},
	basePlusLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.basePlus.fontSize,
		lineHeight: typescale.basePlus.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.basePlusMobile.fontSize,
			lineHeight: typescale.basePlusMobile.lineHeight,
		},
	},
	basePlusRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.basePlus.fontSize,
		lineHeight: typescale.basePlus.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.basePlusMobile.fontSize,
			lineHeight: typescale.basePlusMobile.lineHeight,
		},
	},
	basePlusSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.basePlus.fontSize,
		lineHeight: typescale.basePlus.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.basePlusMobile.fontSize,
			lineHeight: typescale.basePlusMobile.lineHeight,
		},
	},
	baseLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.base.fontSize,
		lineHeight: typescale.base.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.baseMobile.fontSize,
			lineHeight: typescale.baseMobile.lineHeight,
		},
	},
	baseRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.base.fontSize,
		lineHeight: typescale.base.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.baseMobile.fontSize,
			lineHeight: typescale.baseMobile.lineHeight,
		},
	},
	baseSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.base.fontSize,
		lineHeight: typescale.base.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.baseMobile.fontSize,
			lineHeight: typescale.baseMobile.lineHeight,
		},
	},
	smallLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.small.fontSize,
		lineHeight: typescale.small.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.smallMobile.fontSize,
			lineHeight: typescale.smallMobile.lineHeight,
		},
	},
	smallRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.small.fontSize,
		lineHeight: typescale.small.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.smallMobile.fontSize,
			lineHeight: typescale.smallMobile.lineHeight,
		},
	},
	smallSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.small.fontSize,
		lineHeight: typescale.small.lineHeight,
		[breakpoints.mobile]: {
			fontSize: typescale.smallMobile.fontSize,
			lineHeight: typescale.smallMobile.lineHeight,
		},
	},
	extraSmallLight: {
		fontWeight: typescale.weight.light,
		fontSize: typescale.extraSmall.fontSize,
		lineHeight: typescale.extraSmall.lineHeight,
	},
	extraSmallRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: typescale.extraSmall.fontSize,
		lineHeight: typescale.extraSmall.lineHeight,
	},
	extraSmallSemiBold: {
		fontWeight: typescale.weight.semiBold,
		fontSize: typescale.extraSmall.fontSize,
		lineHeight: typescale.extraSmall.lineHeight,
	},
	eventLabelMedium: {
		fontWeight: 500,
		fontSize: pxToRem(14),
		lineHeight: 20 / 14,
		letterSpacing: 0,
	},
	eventValueRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: pxToRem(16),
		lineHeight: 24 / 16,
		letterSpacing: 0,
	},
	eventValueMedium: {
		fontWeight: 500,
		fontSize: pxToRem(16),
		lineHeight: 24 / 16,
		letterSpacing: 0,
	},
	eventHelperRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: pxToRem(14),
		lineHeight: 20 / 14,
		letterSpacing: 0,
	},
	eventInfoRegular: {
		fontWeight: typescale.weight.regular,
		fontSize: pxToRem(12),
		lineHeight: 16 / 12,
		letterSpacing: 0,
	},
} as const

export enum TypographyVariants {
	tripleLargeLight = 'tripleLargeLight',
	tripleLargeRegular = 'tripleLargeRegular',
	tripleLargeSemiBold = 'tripleLargeSemiBold',
	doubleLargeLight = 'doubleLargeLight',
	doubleLargeRegular = 'doubleLargeRegular',
	doubleLargeSemiBold = 'doubleLargeSemiBold',
	extraLargeLight = 'extraLargeLight',
	extraLargeRegular = 'extraLargeRegular',
	extraLargeSemiBold = 'extraLargeSemiBold',
	largeLight = 'largeLight',
	largeRegular = 'largeRegular',
	largeSemiBold = 'largeSemiBold',
	mediumPlusLight = 'mediumPlusLight',
	mediumPlusRegular = 'mediumPlusRegular',
	mediumPlusSemiBold = 'mediumPlusSemiBold',
	mediumLight = 'mediumLight',
	mediumRegular = 'mediumRegular',
	mediumSemiBold = 'mediumSemiBold',
	basePlusLight = 'basePlusLight',
	basePlusRegular = 'basePlusRegular',
	basePlusSemiBold = 'basePlusSemiBold',
	baseLight = 'baseLight',
	baseRegular = 'baseRegular',
	baseSemiBold = 'baseSemiBold',
	smallLight = 'smallLight',
	smallRegular = 'smallRegular',
	smallSemiBold = 'smallSemiBold',
	extraSmallLight = 'extraSmallLight',
	extraSmallRegular = 'extraSmallRegular',
	extraSmallSemiBold = 'extraSmallSemiBold',
	eventLabelMedium = 'eventLabelMedium',
	eventValueRegular = 'eventValueRegular',
	eventValueMedium = 'eventValueMedium',
	eventHelperRegular = 'eventHelperRegular',
	eventInfoRegular = 'eventInfoRegular',
}

export const typographyVariantMapping = {
	tripleLargeLight: 'h1',
	tripleLargeRegular: 'h1',
	tripleLargeSemiBold: 'h1',
	doubleLargeLight: 'h1',
	doubleLargeRegular: 'h1',
	doubleLargeSemiBold: 'h1',
	extraLargeLight: 'h1',
	extraLargeRegular: 'h1',
	extraLargeSemiBold: 'h1',
	largeLight: 'h1',
	largeRegular: 'h1',
	largeSemiBold: 'h1',
	mediumPlusLight: 'h1',
	mediumPlusRegular: 'h1',
	mediumPlusSemiBold: 'h1',
	mediumLight: 'h2',
	mediumRegular: 'h2',
	mediumSemiBold: 'h2',
	basePlusLight: 'h6',
	basePlusRegular: 'h6',
	basePlusSemiBold: 'h6',
	baseLight: 'h6',
	baseRegular: 'h6',
	baseSemiBold: 'h6',
	smallLight: 'p',
	smallRegular: 'p',
	smallSemiBold: 'p',
	extraSmallLight: 'p',
	extraSmallRegular: 'p',
	extraSmallSemiBold: 'p',
	eventLabelMedium: 'p',
	eventValueRegular: 'p',
	eventValueMedium: 'p',
	eventHelperRegular: 'p',
	eventInfoRegular: 'p',
}
